/**
 * admin_access_control_controller.js — Admin access control page (Stimulus).
 *
 * Ports the former static/js/admin_access_control.js
 * (attachOnce('adminAccessControlListeners') + turbo:load) to an element-scoped
 * controller. The page is a client-side searchable/filterable user table, role
 * chip filters, a role-editor modal with a live effective-permission preview,
 * and a save operation.
 *
 * Lifecycle (replaces attachOnce + turbo:load):
 *   connect() resets filter state, fetches role definitions, and binds the
 *   search input, role chips, select-all, and the document-delegated change
 *   listener that drives the permission preview. disconnect() removes the one
 *   document-delegated listener (the element-bound listeners on the search box /
 *   chips die with the swapped <body>). The controller instance owns add+remove
 *   of the delegated listener, so it cannot stack across Turbo navigations — the
 *   failure attachOnce hand-rolled. connect() fires per element on each Turbo
 *   navigation, so element listeners always attach to fresh DOM.
 *
 * The editor/save/preview functions (openEditor, saveRoles,
 * updateEffectivePreview, escapeHtml) stay as module functions; openEditor +
 * saveRoles remain exposed on window for the data-action="call-fn" dispatcher.
 * ROLE_DEFINITIONS / _acActiveRoles stay as module-level state.
 *
 * data-controller="admin-access-control" hosts on the page's container-fluid.
 * Template: templates/admin/access_control.html
 */

import { Controller } from "@hotwired/stimulus";

    // Role definitions (from code registry) for client-side effective permission preview
    var ROLE_DEFINITIONS = {};
    var _acActiveRoles = [];

    // ─── Client-side filter ──────────────────────────────────────────
    function _acFilterUsers() {
        var searchEl = document.getElementById('acSearch');
        if (!searchEl) return;
        var searchTerm = searchEl.value.toLowerCase().trim();

        var rows = document.querySelectorAll('#acUserTableBody tr[data-user-id]');
        var visibleCount = 0;
        rows.forEach(function (row) {
            var matchesSearch = !searchTerm || (row.dataset.search || '').includes(searchTerm);
            var matchesRole = true;
            if (_acActiveRoles.length > 0) {
                var userRoles = (row.dataset.roles || '').trim().split(/\s+/);
                matchesRole = _acActiveRoles.some(function (r) { return userRoles.indexOf(r) > -1; });
            }
            var visible = matchesSearch && matchesRole;
            row.style.display = visible ? '' : 'none';
            if (visible) visibleCount++;
        });
        var countEl = document.getElementById('acUserCount');
        if (countEl) countEl.textContent = visibleCount;
    }

    function _acUpdateSelectAllBtn() {
        var selectAllBtn = document.querySelector('.ac-role-select-all');
        var allChips = document.querySelectorAll('.role-chip');
        if (!selectAllBtn) return;
        if (_acActiveRoles.length === allChips.length) {
            selectAllBtn.textContent = 'None';
            selectAllBtn.classList.add('active');
        } else {
            selectAllBtn.textContent = 'All';
            selectAllBtn.classList.remove('active');
        }
    }

    // ─── Role editor modal ──────────────────────────────────────────
    function openEditor(userId) {
        // Handle data-entity-id attribute
        if (typeof userId === 'object' && userId !== null) {
            userId = userId.target ? userId.target.closest('[data-entity-id]').dataset.entityId : userId;
        }

        fetch('/admin/access-control/users/' + userId)
            .then(function (r) { return r.json(); })
            .then(function (data) {
                if (!data.success) return;
                var u = data.user;

                document.getElementById('editorUserId').value = u.id;
                document.getElementById('editorUserName').textContent = u.display_name + ' (' + u.email + ')';

                // Set role checkboxes
                document.querySelectorAll('.role-check').forEach(function (cb) {
                    cb.checked = (u.system_roles || []).indexOf(cb.value) > -1;
                });

                // Set override selects
                var overrides = u.system_permission_overrides || {};
                document.querySelectorAll('.override-select').forEach(function (sel) {
                    var perm = sel.dataset.perm;
                    if (perm in overrides) {
                        sel.value = overrides[perm] ? 'grant' : 'deny';
                    } else {
                        sel.value = 'inherit';
                    }
                });

                updateEffectivePreview();

                var modal = new bootstrap.Modal(document.getElementById('roleEditorModal'));
                modal.show();
            });
    }

    function updateEffectivePreview() {
        var selectedRoles = [];
        document.querySelectorAll('.role-check:checked').forEach(function (cb) { selectedRoles.push(cb.value); });

        // Compute union of role permissions
        var allPerms = {};
        selectedRoles.forEach(function (role) {
            var def = ROLE_DEFINITIONS[role];
            if (def) {
                def.permissions.forEach(function (p) { allPerms[p] = true; });
            }
        });

        // Apply overrides
        document.querySelectorAll('.override-select').forEach(function (sel) {
            var perm = sel.dataset.perm;
            if (sel.value === 'grant') allPerms[perm] = true;
            else if (sel.value === 'deny') delete allPerms[perm];
        });

        var container = document.getElementById('effectivePermsPreview');
        var permKeys = Object.keys(allPerms).sort();
        if (permKeys.length === 0) {
            container.innerHTML = '<span class="text-muted">No permissions</span>';
        } else {
            container.innerHTML = permKeys.map(function (p) {
                return '<span class="badge bg-success perm-badge me-1 mb-1">' + escapeHtml(p) + '</span>';
            }).join('');
        }
    }

    // ─── Save roles + overrides ─────────────────────────────────────
    function saveRoles() {
        var userId = document.getElementById('editorUserId').value;

        // Collect roles
        var roles = [];
        document.querySelectorAll('.role-check:checked').forEach(function (cb) { roles.push(cb.value); });

        // Collect overrides (skip "inherit")
        var overrides = {};
        document.querySelectorAll('.override-select').forEach(function (sel) {
            if (sel.value === 'grant') overrides[sel.dataset.perm] = true;
            else if (sel.value === 'deny') overrides[sel.dataset.perm] = false;
        });

        // Send both updates in parallel
        Promise.all([
            fetch('/admin/access-control/users/' + userId + '/roles', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roles: roles })
            }),
            fetch('/admin/access-control/users/' + userId + '/overrides', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ overrides: overrides })
            })
        ])
        .then(function (responses) { return Promise.all(responses.map(function (r) { return r.json(); })); })
        .then(function (results) {
            var allOk = results.every(function (r) { return r.success; });
            if (allOk) {
                var modal = bootstrap.Modal.getInstance(document.getElementById('roleEditorModal'));
                modal.hide();
                // Re-render the server's rows in place; the page check waits
                // for the closing modal before it swaps.
                window.chippiePageCheck.refresh();
            } else {
                var err = results.find(function (r) { return !r.success; });
                window.showErrorMessage('Error: ' + (err.error || 'Unknown error'));
            }
        })
        .catch(function (err) {
            window.showErrorMessage('Network error: ' + err.message);
        });
    }

    // ─── Helpers ─────────────────────────────────────────────────────
    function escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
    }

    // Expose for template dispatcher (data-action="call-fn" in global-actions.js).
    window.openEditor = openEditor;
    window.saveRoles = saveRoles;

export default class extends Controller {
        connect() {
            _acActiveRoles = [];

            var searchEl = document.getElementById('acSearch');
            if (!searchEl) return;

            // Fetch role definitions for the effective permission preview in the editor
            fetch('/admin/access-control/roles')
                .then(function (r) { return r.json(); })
                .then(function (data) {
                    if (data.success) ROLE_DEFINITIONS = data.roles;
                });

            // Search input
            searchEl.addEventListener('input', _acFilterUsers);

            // Role chip filters
            document.querySelectorAll('.role-chip').forEach(function (chip) {
                chip.addEventListener('click', function () {
                    var role = this.dataset.role;
                    var idx = _acActiveRoles.indexOf(role);
                    if (idx > -1) { _acActiveRoles.splice(idx, 1); this.classList.remove('active'); }
                    else { _acActiveRoles.push(role); this.classList.add('active'); }
                    _acUpdateSelectAllBtn();
                    _acFilterUsers();
                });
            });

            var selectAllBtn = document.querySelector('.ac-role-select-all');
            if (selectAllBtn) {
                selectAllBtn.addEventListener('click', function () {
                    var allChips = document.querySelectorAll('.role-chip');
                    if (_acActiveRoles.length === allChips.length) {
                        _acActiveRoles = [];
                        allChips.forEach(function (c) { c.classList.remove('active'); });
                    } else {
                        _acActiveRoles = [];
                        allChips.forEach(function (c) { _acActiveRoles.push(c.dataset.role); c.classList.add('active'); });
                    }
                    _acUpdateSelectAllBtn();
                    _acFilterUsers();
                });
            }

            // ─── Client-side effective permission preview ───────────────────
            // Document-delegated because override-selects / role-checks live in a
            // modal. Owned by this controller (add here, remove in disconnect) so
            // it cannot stack across Turbo navigations.
            this._onPreviewChange = function (e) {
                if (e.target.classList.contains('role-check') || e.target.classList.contains('override-select')) {
                    updateEffectivePreview();
                }
            };
            document.addEventListener('change', this._onPreviewChange);
        }

        disconnect() {
            if (this._onPreviewChange) {
                document.removeEventListener('change', this._onPreviewChange);
                this._onPreviewChange = null;
            }
        }
}
